import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useRef } from 'react';
import { Button, Dialog, DialogSurface, makeStyles } from '@fluentui/react-components';
import { ShellIcons } from './shell/icons.js';
import { popupMedia, popupMetrics } from './shell/tokens.js';
import { tokens } from './tokens.js';
const useStyles = makeStyles({
    // A DIALOG, drawn against the right edge and as tall as the window (#585, Figma `1097:1222`). Fluent centres a dialog surface
    // with `inset: 0` and `margin: auto`; these put it at the side instead. Under the stacking width it fills the window.
    surface: {
        top: 0, right: 0, bottom: 0, left: 'auto',
        margin: 0, padding: 0,
        width: popupMetrics.small, maxWidth: '100vw', height: '100%', maxHeight: '100vh',
        borderRadius: tokens.borderRadiusNone,
        display: 'flex', flexDirection: 'column',
        [popupMedia.stack]: { width: '100vw' },
    },
    head: {
        display: 'flex', alignItems: 'flex-start', gap: tokens.spacingHorizontalM, flexShrink: 0,
        paddingBlock: tokens.spacingVerticalXL, paddingInline: tokens.spacingHorizontalXXL,
    },
    headMain: { flexGrow: 1, minWidth: 0 },
    body: { flexGrow: 1, minHeight: 0, overflowY: 'auto', paddingInline: tokens.spacingHorizontalXXL },
    foot: {
        display: 'flex', justifyContent: 'flex-end', gap: tokens.spacingHorizontalM, flexShrink: 0, flexWrap: 'wrap',
        paddingBlock: tokens.spacingVerticalL, paddingInline: tokens.spacingHorizontalXXL,
        borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    },
});
/**
 * A quick look at one record, over the page it was opened from (#585).
 *
 * It is a dialog, not a fourth kind of pop-up: the same Fluent `Dialog` every other pop-up is built on, with its surface at the
 * right edge. So the page behind is hidden from a screen reader while it is open, the keyboard stays inside it, and Esc and a
 * click outside close it. It holds no form, so there is nothing to discard and nothing to ask.
 *
 * The keyboard returns to `opener` when it closes. That is deferred by one tick for the reason `PopupDialog` gives: Fluent's
 * modalizer is still active for a moment after `open` turns false.
 */
export function SidePanel({ open, ariaLabel, onClose, opener, header, footer, children }) {
    const styles = useStyles();
    const returnTo = useRef(null);
    useLayoutEffect(() => {
        if (open)
            returnTo.current = opener?.current ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    }, [open, opener]);
    const wasOpen = useRef(false);
    useEffect(() => {
        if (open) {
            wasOpen.current = true;
            return;
        }
        if (wasOpen.current) {
            wasOpen.current = false;
            const back = returnTo.current;
            setTimeout(() => back?.focus(), 0);
        }
    }, [open]);
    return (_jsx(Dialog, { open: open, modalType: "modal", onOpenChange: (_, data) => { if (!data.open)
            onClose(); }, children: _jsxs(DialogSurface, { className: styles.surface, "aria-label": ariaLabel, children: [_jsxs("div", { className: styles.head, children: [_jsx("div", { className: styles.headMain, children: header }), _jsx(Button, { appearance: "subtle", "aria-label": "Close", icon: _jsx(ShellIcons.close, {}), onClick: onClose })] }), _jsx("div", { className: styles.body, children: children }), footer === undefined ? null : _jsx("div", { className: styles.foot, children: footer })] }) }));
}
//# sourceMappingURL=SidePanel.js.map